import { useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Loader2, List, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(amount || 0);
};

const SortableHeader = ({ label, field, sortField, sortDir, onSort, align = 'left' }) => {
  const active = sortField === field;
  const Icon = active ? (sortDir === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
  return (
    <TableHead
      className={`font-semibold cursor-pointer select-none hover:bg-slate-200 transition-colors ${align === 'right' ? 'text-right' : ''}`}
      onClick={() => onSort(field)}
      data-testid={`drilldown-sort-${field}`}
    >
      <span className={`inline-flex items-center gap-1 ${align === 'right' ? 'flex-row-reverse' : ''}`}>
        {label}
        <Icon className={`w-3.5 h-3.5 ${active ? 'text-slate-900' : 'text-slate-400'}`} />
      </span>
    </TableHead>
  );
};

const DrillDownDialog = ({ 
  open, 
  onOpenChange, 
  categoryName, 
  transactions, 
  total, 
  loading 
}) => {
  const [sortField, setSortField] = useState('date');
  const [sortDir, setSortDir] = useState('asc');
  
  const hasCategories = transactions.some(t => t.category_name);

  const handleSort = (field) => {
    if (field === sortField) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      // Numeric/date fields default to descending on first click; strings ascending
      setSortDir(field === 'amount' ? 'desc' : 'asc');
    }
  };
  
  // Rows enriched with the running Balance (computed chronologically) then sorted
  // by the user's chosen column. Balance always reflects the balance AT THE TIME
  // of each transaction regardless of current sort order.
  const displayRows = useMemo(() => {
    const chronological = [...transactions].sort((a, b) =>
      (a.date || '').localeCompare(b.date || '')
    );
    let running = 0;
    const enriched = chronological.map(txn => {
      running += txn.amount || 0;
      return { ...txn, _balance: running };
    });

    const dir = sortDir === 'asc' ? 1 : -1;
    const getKey = (t) => {
      switch (sortField) {
        case 'date': return t.date || '';
        case 'reference': return (t.reference_number || '').toString().toLowerCase();
        case 'description': {
          const v = t.vendor_name || '';
          const d = t.description || '';
          return `${v} ${d}`.trim().toLowerCase();
        }
        case 'category': return (t.category_name || '').toLowerCase();
        case 'amount': return t.amount || 0;
        default: return '';
      }
    };
    return [...enriched].sort((a, b) => {
      const ka = getKey(a); const kb = getKey(b);
      if (typeof ka === 'number' && typeof kb === 'number') return (ka - kb) * dir;
      if (ka < kb) return -1 * dir;
      if (ka > kb) return 1 * dir;
      return 0;
    });
  }, [transactions, sortField, sortDir]);
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[900px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl flex items-center gap-2">
            <List className="w-5 h-5" />
            {categoryName} - Transaction Details
          </DialogTitle>
          <DialogDescription>
            {transactions.length} transaction(s) • Total: {formatCurrency(total)}
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4">
          {loading ? (
            <div className="py-8 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-amber-700 mx-auto" />
              <p className="text-slate-500 mt-4">Loading transactions...</p>
            </div>
          ) : transactions.length === 0 ? (
            <div className="py-8 text-center">
              <p className="text-slate-500">No transactions found for this category</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-100">
                  <SortableHeader label="Date" field="date" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  <SortableHeader label="Ref #" field="reference" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  <SortableHeader label="Description / Vendor" field="description" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  {hasCategories && (
                    <SortableHeader label="Category" field="category" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  )}
                  <SortableHeader label="Amount" field="amount" sortField={sortField} sortDir={sortDir} onSort={handleSort} align="right" />
                  <TableHead className="text-right font-semibold">Balance</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayRows.map((txn, index) => (
                  <TableRow key={txn.id || index} className="hover:bg-slate-50">
                    <TableCell className="font-mono text-sm">{txn.date}</TableCell>
                    <TableCell className="font-mono text-sm text-slate-600">{txn.reference_number || '-'}</TableCell>
                    <TableCell>
                      {txn.vendor_name && <span className="font-medium">{txn.vendor_name}</span>}
                      {txn.vendor_name && txn.description && ' - '}
                      {txn.description && <span className="text-slate-600">{txn.description}</span>}
                      {!txn.vendor_name && !txn.description && <span className="text-slate-400">-</span>}
                    </TableCell>
                    {hasCategories && <TableCell className="text-sm text-slate-600">{txn.category_name || '-'}</TableCell>}
                    <TableCell className={`text-right font-mono ${txn.amount >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                      {formatCurrency(txn.amount)}
                    </TableCell>
                    <TableCell className="text-right font-mono font-medium text-slate-800">
                      {formatCurrency(txn._balance ?? 0)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export { DrillDownDialog };
